<template>
  <div class="detection-item" :class="{ editing: isEditing }">
    <span class="color-dot" :style="{ backgroundColor: detection.color }" />

    <div class="detection-info">
      <span class="detection-name">{{ detection.pathology }}</span>
      <span class="detection-type">
        <font-awesome-icon :icon="typeIcon" class="type-icon" />
        {{ typeLabel }}
        <span v-if="!detection.isOriginal" class="badge-new">New</span>
      </span>
    </div>

    <div class="detection-actions">
      <button
        v-if="!detection.isDiagnosis"
        type="button"
        class="action-btn"
        :class="{ active: isEditing }"
        :title="isEditing ? 'Save changes to this finding' : 'Edit coordinates'"
        @click.prevent="$emit('edit', detection.id)"
      >
        <font-awesome-icon
          :icon="isEditing ? ['fas', 'check'] : ['fas', 'pen']"
        />
      </button>
      <!-- While editing this finding, the delete button is replaced by a
           "cancel session" (X) so edits can be reverted cleanly without undo. -->
      <button
        v-if="isEditing && !detection.isDiagnosis"
        type="button"
        class="action-btn cancel-edit-btn"
        title="Cancel edits to this finding"
        @click.prevent="$emit('cancel-edit', detection.id)"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
      <button
        v-else
        type="button"
        class="action-btn delete-btn"
        title="Delete detection"
        @click.prevent="$emit('delete', detection.id)"
      >
        <font-awesome-icon :icon="['fas', 'trash']" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  detection: {
    type: Object,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["edit", "delete", "cancel-edit"]);

const typeLabel = computed(() => {
  if (props.detection.isDiagnosis) return "Diagnosis";
  return props.detection.type === "box" ? "Bounding Box" : "Freehand";
});

const typeIcon = computed(() => {
  if (props.detection.isDiagnosis) return ["fas", "tag"];
  return props.detection.type === "box"
    ? ["fas", "square"]
    : ["fas", "pencil"];
});
</script>

<style scoped>
.detection-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: var(--border-radius-small);
  border: 1px solid transparent;
  background-color: var(--color-bg-secondary);
  transition: all 0.15s ease;
}

.detection-item:hover {
  border-color: var(--color-border-primary);
}

.detection-item.editing {
  border-color: var(--color-accent-primary);
  background-color: var(--color-bg-tertiary, var(--color-bg-secondary));
  box-shadow: 0 0 0 1px var(--color-accent-primary);
}

.color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.3);
}

.detection-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.detection-name {
  font-size: 0.85rem;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detection-type {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.type-icon {
  font-size: 0.65rem;
}

.badge-new {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  padding: 0.05rem 0.3rem;
  border-radius: var(--border-radius-small);
  font-size: 0.6rem;
  font-weight: 600;
}

.detection-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-small);
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.15s ease;
}

.action-btn:hover {
  border-color: var(--color-border-hover);
}

.action-btn.active {
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  border-color: var(--color-accent-primary);
}

.delete-btn:hover {
  background-color: var(--color-danger, #dc3545);
  color: #fff;
  border-color: var(--color-danger, #dc3545);
}

.cancel-edit-btn:hover {
  color: var(--color-accent-primary, #ffc107);
  border-color: var(--color-accent-primary, #ffc107);
}
</style>
